import { useEffect, useState, useCallback, useMemo } from "react";
import { getOnedata } from "../controllers/APIController";

// Get the all organizations list of current User Logged into Application
export default function useGetAllOrganization() {
  const [OrgList, setOrgList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Memoize user data to prevent unnecessary re-renders
  const UserLogged = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userData"));
    } catch (error) {
      console.error("Error parsing user data from session storage:", error);
      return null;
    }
  }, []);
  
  const UserId = useMemo(() => UserLogged?.user?.id, [UserLogged]);

  // Memoize the fetch function to prevent recreation on every render
  const fetchOrganizations = useCallback(async (isRetry = false) => {
    console.log("🔄 useGetAllOrganization: fetchOrganizations called", { 
      UserId, 
      hasUserData: !!UserLogged,
      isRetry,
      retryCount: retryCount + (isRetry ? 1 : 0)
    });
    
    if (!UserId) {
      console.log("🔄 useGetAllOrganization: No UserId, skipping fetch");
      setIsLoading(false);
      setOrgList([]);
      return;
    }

    try {
      if (!isRetry) {
        setIsLoading(true);
        setErr(null);
      }
      
      console.log("🔄 useGetAllOrganization: Starting fetch for user", UserId);
      const data = await getOnedata("users", UserId);
      
      // Debug: Log raw data to identify duplicates
      if (data?.OrganizationLists) {
        console.log("🔄 useGetAllOrganization: Raw organization data:", data.OrganizationLists);
        const urls = data.OrganizationLists.map(item => item?.URL).filter(Boolean);
        const uniqueUrls = [...new Set(urls)];
        if (urls.length !== uniqueUrls.length) {
          console.warn(`🔄 useGetAllOrganization: Found ${urls.length - uniqueUrls.length} duplicate URLs in raw data`);
        }
      }
      
      // Handle successful response
      if (data?.OrganizationLists && Array.isArray(data.OrganizationLists)) {
        const list = data.OrganizationLists
          .filter(element => element?.URL) // Filter out undefined/null URLs
          .map(element => element.URL);
        
        // Use Set for efficient deduplication while preserving order
        const uniqueUrls = [];
        const seen = new Set();
        
        for (const url of list) {
          if (!seen.has(url)) {
            seen.add(url);
            uniqueUrls.push(url);
          }
        }
        
        // Only log if there were actual duplicates
        if (uniqueUrls.length < list.length) {
          console.log(`🔄 useGetAllOrganization: Cleaned ${list.length - uniqueUrls.length} duplicate URLs, keeping ${uniqueUrls.length} unique organizations`);
        }
        
        console.log("🔄 useGetAllOrganization: Fetched organizations", { count: uniqueUrls.length, list: uniqueUrls });
        setOrgList(uniqueUrls);
        setErr(null);
        setRetryCount(0); // Reset retry count on success
      } else {
        console.log("🔄 useGetAllOrganization: No organization lists found or invalid data structure", data);
        setOrgList([]);
        setErr(null);
      }
    } catch (error) {
      console.error("🔄 useGetAllOrganization: Error fetching organizations:", error);
      
      // Handle different types of errors
      if (error.message?.includes('500') || error.message?.includes('socket hang up')) {
        if (retryCount < 3) {
          console.log(`🔄 useGetAllOrganization: Server error, will retry (${retryCount + 1}/3)`);
          setRetryCount(prev => prev + 1);
          setErr({ ...error, retryable: true, retryCount: retryCount + 1 });
          
          // Auto-retry after delay
          setTimeout(() => {
            fetchOrganizations(true);
          }, 2000 * (retryCount + 1)); // Exponential backoff
          return;
        } else {
          console.log("🔄 useGetAllOrganization: Max retries reached, using fallback");
          setErr({ ...error, retryable: false, message: "Max retries reached. Please refresh the page." });
        }
      } else {
        setErr(error);
      }
      
      // Set fallback data to prevent crashes
      setOrgList([]);
    } finally {
      if (!isRetry) {
        setIsLoading(false);
      }
    }
  }, [UserId, UserLogged, retryCount]);

  // Manual retry function
  const retryFetch = useCallback(() => {
    setRetryCount(0);
    setErr(null);
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (UserId) {
      fetchOrganizations();
    } else {
      setIsLoading(false);
      setOrgList([]);
    }
  }, [fetchOrganizations, UserId]);

  // Return memoized values to prevent unnecessary re-renders
  return useMemo(() => ({ 
    OrgList, 
    isLoading, 
    err,
    retryCount,
    refetch: fetchOrganizations,
    retry: retryFetch
  }), [OrgList, isLoading, err, retryCount, fetchOrganizations, retryFetch]);
}
